import { gql } from '@apollo/client';
import {
  recommendationCompanyDetails,
  submittedReferenceRequestDetails,
  sectionComponentDetails,
  revieweeDetails,
  behaviorScores,
  behaviorProfileDetails,
  statusType,
  formComponentGroup,
  CPR_ATTRIBUTE,
  hirerProfileDetails,
  positionReferenceRequirements,
  referenceRequirementSyntax,
} from './sharedApiConstants';
import { CANDIDATE_PROFILE_DETAILS_FRAGMENT, CPR_ATTRIBUTE_ON_CPR } from './fragments';

export const workRelationGroupName = 'answer_relationship';

const candidateAccessibleReferencerRelationDetails = `
  candidateReferencerRelationId
  candidateExperienceId
  referencerTitle
  timeOverlap
  workRelationship
  referencerEmail
  referencerFirstName
  referencerLastName
  isArchived
  isPublic
  overlapStartDate
  overlapEndDate
  contextForReference
  hasRecommendation
  details
  referenceRequests {
    referenceRequestId
    referencerInvitedDate
    submissionDate
    publicSummary {
      answerId
      answerBody
    }
    ${statusType}
  }
  referencerProfile {
    referencerProfileId
    referencerEmail
    user {
      userId
      firstName
      lastName
    }
  }
`;

export const candidateAccessibleExperienceDetails = `
  candidateExperienceId
  order
  positionTitle
  companyName
  locationCity
  locationState
  locationCountry
  startMonth
  startYear
  endMonth
  endYear
  isPresent
  details
  reviewCycleId
  experienceType {
    experienceTypeId
    typeName
  }
  experienceSection {
    experienceSectionId
  }
  experienceDetails {
    experienceDetailId
    componentText
    order
  }
  candidateReferencerRelations {
    ${candidateAccessibleReferencerRelationDetails}
    hasRecommendation
    recommendation {
      recommendationId
    }
  }
`;

export const LOGIN_CANDIDATE_USER = gql`
  mutation signInUser($input: SignInUserInput!) {
    signInUser(input: $input) {
      token
      refreshToken
      permissions
      tempLogin
      user {
        userId
        firstName
        username
        primaryEmail
        lastName
        permissions
        isArchived
        candidateProfile {
          candidateProfileId
        }
      }
    }
  }
`;

export const ADD_CANDIDATE_REFERENCER_RELATION = gql`
  mutation addCandidateReferencerRelation($input: NewCandidateReferencerRelationInput!) {
    addCandidateReferencerRelation(input: $input) {
      ${candidateAccessibleReferencerRelationDetails}
    }
  }
`;

export const UPDATE_CANDIDATE_REFERENCER_RELATION = gql`
  mutation updateCandidateReferencerRelation($input: UpdateCandidateReferencerRelationInput!) {
    updateCandidateReferencerRelation(input: $input) {
      ${candidateAccessibleReferencerRelationDetails}
    }
  }
`;

export const SEND_REFERENCER_INVITE = gql`
  mutation InviteReferencerWithCandidateRelation(
    $input: InviteReferencerWithCandidateRelationInput!
  ) {
    inviteReferencerWithCandidateRelation(input: $input) {
      candidateReferencerRelationId
      referenceRequests {
        referenceRequestId
        referencerInvitedDate
        ${statusType}
      }
    }
  }
`;

export const FETCH_CANDIDATE_EXPERIENCE_SECTION_HEADERS = gql`
  query experienceSectionsForMe {
    experienceSectionsForMe {
      experienceSectionId
      componentText
      order
      reviewCycleId
      candidateExperiences {
        ${candidateAccessibleExperienceDetails}
      }
    }
  }
`;

export const FIND_CANDIDATE_INVITATION_WITH_CODE = gql`
  query FetchCPRDetailsForAccountCreation($input: ValidateCandidateInvitationInput!) {
    fetchCPRDetailsForAccountCreation(input: $input) {
      candidatePositionRelationId
      positionTitle
      companyName
      firstName
      lastName
      positionId
      isAssessment360Position
      candidateProfileId
      candidateEmail
      isArchived
      statusName
      showPositionTitleToCandidate
    }
  }
`;

export const CREATE_CANDIDATE_ACCOUNT_FOR_EXISTING_CPR = gql`
  mutation CreateCandidateAccountForExistingCPR(
    $input: CreateCandidateAccountForExistingCPRInput!
  ) {
    createCandidateAccountForExistingCPR(input: $input) {
      token
      refreshToken
      permissions
      user {
        userId
        firstName
        lastName
        username
        primaryEmail
        isArchived
        candidateProfile {
          candidateProfileId
          candidateEmail
          isArchived
          permissions
          candidatePositionRelations {
            candidatePositionRelationId
            statusTypeId
            position {
              positionId
              positionIdentifier
            }
          }
        }
      }
    }
  }
`;

export const SEND_TEST_CANDIDATE_INVITATION = gql`
  mutation SendTestCandidateInvite($input: CreateAndInviteCandidatePositionRelationsInput!) {
    sendTestCandidateInvite(input: $input)
  }
`;

export const UPDATE_BULK_RECOMMENDATION_COMPANIES_FOR_CANDIDATE = gql`
  mutation UpdateBulkRecommendationCompaniesForCandidate (
    $input: BulkUpdateRecommendationCompaniesInput!
  ) {
    updateBulkRecommendationCompaniesForCandidate (input: $input) {
      ${recommendationCompanyDetails}
    }
  }
`;

const position = `
  position {
    positionId
    positionTitle
    numRequiredReferences
    isCandidateInitiatedPosition
    candidateViewFullReport
    subjectCanInviteRespondent
    hideRespondentName
    candidateFormComponentGroup {
      formComponentGroupId
      componentText
    }
    reviewCycleId
    company {
      companyId
      companyName
      companyLogo
      showPositionTitleToCandidate
    }
    ${positionReferenceRequirements}
    referenceRequirementSyntax {
      ${referenceRequirementSyntax}
    }
  }
`;

const scoreData = `
  cprAttributes {
    ${CPR_ATTRIBUTE}
  }
`;

export const SEARCH_CANDIDATE_POSITION_RELATION_REQUIREMENTS = gql`
  query FetchCandidatePositionRelationsForMe($input: FetchCandidatePositionRelationsForMeInput!) {
    fetchCandidatePositionRelationsForMe(input: $input) {
      candidatePositionRelationId
      ${scoreData}
      ${position}
    }
  }
`;

export const FETCH_POSITION_DETAILS_FOR_ACCOUNT_CREATION = gql`
  query fetchPositionDetailsForAccountCreation(
    $input: FetchPositionDetailsForAccountCreationInput!
  ) {
    fetchPositionDetailsForAccountCreation(input: $input) {
      positionId
      positionTitle
      companyName
      companyId
      positionIdentifier
      isAssessment360Position
      showPositionTitleToCandidate
    }
  }
`;

export const UPDATE_CANDIDATE_EXPERIENCE_FOR_ME = gql`
  mutation UpdateCandidateExperienceForMe ($input: UpdateCandidateExperienceInput!) {
    updateCandidateExperienceForMe (input: $input)
    {
      ${candidateAccessibleExperienceDetails}
    }
  }
`;

const signInUserDetails = `
token
refreshToken
permissions
tempLogin
user {
  userId
  firstName
  username
  primaryEmail
  lastName
  permissions
  isArchived
  hirerProfile {
    ${hirerProfileDetails}
  }
  referencerProfile {
    referencerProfileId
    referencerEmail
    isArchived
    canAddRecommendation
  }
  candidateProfile {
    candidateProfileId
    isGamificationCandidate
    isArchived
  }
}
`;

export const ACCEPT_RECOMMENDATION_FOR_CANDIDATE_WITH_GOOGLE = gql`
  mutation acceptRecommendationForCandidateWithGoogle (
    $input: AcceptRecommendationForCandidateWithGoogleInput!
  ) {
    acceptRecommendationForCandidateWithGoogle (input: $input) {
      ${signInUserDetails}
    }
  }
`;

export const SIGNUP_CANDIDATE_AND_CREATE_CPR_WITH_GOOGLE = gql`
  mutation signUpCandidateAndCreateCPRWithGoogle (
    $input: SignUpCandidateAndCreateCPRWithGoogleInput!
  ) {
    signUpCandidateAndCreateCPRWithGoogle (input: $input) {
      ${signInUserDetails}
    }
  }
`;

export const SIGNUP_CANDIDATE_FOR_CPR_WITH_GOOGLE = gql`
  mutation signUpCandidateForCPRWithGoogle ($input: SignUpCandidateForCPRWithGoogleInput!) {
    signUpCandidateForCPRWithGoogle (input: $input) {
      ${signInUserDetails}
    }
  }
`;

export const GENERATE_ONE_PAGER_REPORT_FOR_CANDIDATE = gql`
  query generateOnePagerForCandidate($input: CandidatePdfReportInput!) {
    generateOnePagerForCandidate(input: $input) {
      fileType
      base64Data
    }
  }
`;

export const UPDATE_CANDIDATE_PROFILE_PICTURE = gql`
  mutation updateCandidatePicture($input: UpdateCandidateAvatarInput!) {
    updateCandidatePicture(input: $input) {
      candidateProfileId
      candidatePhoto
    }
  }
`;

export const GET_OPPORTUNITY_DETAILS_FOR_CANDIDATE = gql`
  query getOpportunityForCandidate {
    getOpportunityForCandidate {
      candidatePositionRelationId
      opportunityId
      candidateProfileId
      candidateInvitedDate
      candidateFirstName
      candidateLastName
      candidateEmail
      isArchived
      statusTypeId
      numReferencesCompleted
      numReferencesInvited
      positionId
      ${behaviorScores}
      ${statusType}
      behaviorProfile {
        ${behaviorProfileDetails}
      }
      referenceStatus {
        statusTypeId
        typeName
      }
      ...cprAttributes
      candidateProfile {
        ...CandidateProfileDetails
      }
      position {
        positionId
        positionTitle
        companyId
        formComponentGroup {
          formComponentGroupId
          formIdentifier
          componentText
          sectionComponents {
            ${sectionComponentDetails}
          }
        }
        company {
          companyId
          companyName
          companyLogo
          showPositionTitleToCandidate
        }
      }
    }
  }
  ${CPR_ATTRIBUTE_ON_CPR}
  ${CANDIDATE_PROFILE_DETAILS_FRAGMENT}
`;

export const GET_REFERENCE_REQUESTS_FOR_CANDIDATE = gql`
  query getOpportunityForCandidate {
    getOpportunityForCandidate {
      candidatePositionRelationId
      pendingAndSubmittedReferenceRequests {
        pendingReferenceRequests {
          referenceRequestId
        }
        submittedReferenceRequests {
          ${submittedReferenceRequestDetails}
          formComponentGroup {
            ${formComponentGroup}
          }
        }
      }
    }
  }
`;

export const GENERATE_FULL_REPORT_FOR_CANDIDATE = gql`
  query generateFullReportForCandidate {
    generateFullReportForCandidate {
      fileType
      base64Data
    }
  }
`;

export const FETCH_APPLIED_COMPANIES_FOR_ME = gql`
  query FetchCandidatePositionRelationsForMe($input: FetchCandidatePositionRelationsForMeInput!) {
    fetchCandidatePositionRelationsForMe(input: $input) {
      candidatePositionRelationId
      position {
        positionId
        company {
          companyId
          companyName
          companyLogo
          whitelistedEmailDomain
        }
      }
    }
  }
`;

export const UPDATE_CANDIDATE_IS_PUBLIC_FIELD = gql`
  mutation updateCandidateIsPublic($input: UpdateCandidateIsPublicInput!) {
    updateCandidateIsPublic(input: $input) {
      candidateProfileId
      isPublic
    }
  }
`;

export const UPDATE_REFERENCE_IS_PUBLIC = gql`
  mutation updateReferenceIsPublic($input: UpdateReferenceIsPublicInput!) {
    updateReferenceIsPublic(input: $input) {
      candidateReferencerRelationId
      isPublic
    }
  }
`;

export const QUERY_CANDIDATE_REFERENCER_RELATIONS_FOR_MY_CANDIDATE_EXPERIENCE = gql`
  query candidateReferencerRelationsForMyExperience(
    $input: CandidateReferencerRelationsForMyExperienceInput!
  ) {
    candidateReferencerRelationsForMyExperience(input: $input) {
      ${candidateAccessibleReferencerRelationDetails}
    }
  }
`;

export const GET_OR_CREATE_CANDIDATE_FOR_POSITION = gql`
  mutation getOrCreateCandidateForPosition($input: GetOrCreateCandidateForPositionInput!) {
    getOrCreateCandidateForPosition(input: $input) {
      candidatePositionRelationId
      candidateEmail
      candidateFirstName
      candidateLastName
      candidateProfile {
        candidateProfileId
        candidateEmail
        user {
          userId
          username
          firstName
          lastName
        }
      }
    }
  }
`;

export const FETCH_REVIEW_CYCLE_CPRS_FOR_ME = gql`
  query fetchReviewCycleCPRsForMe {
    fetchReviewCycleCPRsForMe {
      candidatePositionRelationId
      ${position}
      ${scoreData}
      behaviorProfile {
        ${behaviorProfileDetails}
      }
      reviewee {
        ${revieweeDetails}
      }
      candidateInvitedDate
    }
  }
`;

export const FETCH_REFERENCE_CPRS_FOR_ME = gql`
  query fetchReferenceCPRsForMe {
    fetchReferenceCPRsForMe {
      candidatePositionRelationId
      ${position}
      ${scoreData}
      behaviorProfile {
        ${behaviorProfileDetails}
      }
    }
  }
`;

export const FETCH_REVIEW_CYCLE_STRENGTHS_FOR_ME = gql`
  query fetchReviewCycleCPRsForMe {
    fetchReviewCycleCPRsForMe {
      candidatePositionRelationId
      position {
        positionId
        reviewCycleId
      }
    }
  }
`;

export const FETCH_REFERENCE_STRENGTHS_FOR_ME = gql`
  query fetchReferenceCPRsForMe {
    fetchReferenceCPRsForMe {
      candidatePositionRelationId
      ${scoreData}
    }
  }
`;

export const ADD_CANDIDATE_EXPERIENCE_FOR_SELF = gql`
  mutation AddCandidateExperienceForSelf($input: NewCandidateExperienceInput!) {
    addCandidateExperienceForMe(input: $input) {
      companyName
      positionTitle
      experienceSection {
        experienceSectionId
        candidateProfile {
          candidateProfileId
          candidateEmail
        }
      }
    }
  }
`;
